import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, CheckCircle2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  painelButtonDestructiveClass,
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
  painelPermissionsModalShellClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';

type DialogTone = 'destructive' | 'success' | 'info';

type ToneConfig = {
  icon: LucideIcon;
  eyebrow: string;
  iconShell: string;
  iconColor: string;
  glow: string;
};

const TONE_CONFIG: Record<DialogTone, ToneConfig> = {
  destructive: {
    icon: Trash2,
    eyebrow: 'Confirmação',
    iconShell: 'bg-gradient-to-br from-rose-500/22 to-rose-700/10 ring-rose-400/30',
    iconColor: 'text-rose-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(244,63,94,0.28)]',
  },
  success: {
    icon: CheckCircle2,
    eyebrow: 'Sucesso',
    iconShell: 'bg-gradient-to-br from-emerald-500/22 to-emerald-700/10 ring-emerald-400/30',
    iconColor: 'text-emerald-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(52,211,153,0.28)]',
  },
  info: {
    icon: AlertTriangle,
    eyebrow: 'Atenção',
    iconShell: 'bg-gradient-to-br from-amber-500/22 to-amber-700/10 ring-amber-400/30',
    iconColor: 'text-amber-200',
    glow: 'shadow-[0_0_40px_-10px_rgba(251,191,36,0.28)]',
  },
};

export function PainelCompactDialogShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('relative', painelPermissionsModalShellClass, className)}>
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.12),transparent)]"
        aria-hidden
      />
      <div className="relative">{children}</div>
    </div>
  );
}

function PainelFeedbackDialogHeader({
  tone,
  title,
  description,
}: {
  tone: DialogTone;
  title: string;
  description: React.ReactNode;
}) {
  const config = TONE_CONFIG[tone];
  const Icon = config.icon;

  return (
    <>
      <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6">
        <div className="flex items-start gap-4">
          <span
            className={cn(
              'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ring-1',
              config.iconShell,
              config.glow
            )}
          >
            <Icon className={cn('h-6 w-6', config.iconColor)} aria-hidden />
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
              {config.eyebrow}
            </p>
            <h2 className="mt-1 font-poppins text-lg font-bold text-white sm:text-xl">{title}</h2>
          </div>
        </div>
      </div>
      <div className="px-6 py-5">
        <p className="font-inter text-sm leading-relaxed text-slate-400">{description}</p>
      </div>
    </>
  );
}

function PainelFeedbackDialogFooter({
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  tone,
}: {
  cancelLabel: string;
  confirmLabel: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  tone: DialogTone;
}) {
  return (
    <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3">
      <AlertDialogCancel asChild>
        <Button
          type="button"
          variant="ghost"
          className={painelModalCancelButtonClass}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </AlertDialogCancel>
      <AlertDialogAction asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            tone === 'destructive' ? painelButtonDestructiveClass : painelButtonPrimaryClass,
            'h-10 px-5 ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-offset-0',
            tone === 'destructive'
              ? 'focus-visible:ring-rose-400/35'
              : 'focus-visible:ring-sky-400/35'
          )}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </AlertDialogAction>
    </div>
  );
}

const panelAlertContentClass =
  'max-w-[440px] gap-0 overflow-hidden border-0 bg-transparent p-0 shadow-none';

export type PainelConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  tone?: Extract<DialogTone, 'destructive' | 'info'>;
};

export function PainelConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  onConfirm,
  tone = 'destructive',
}: PainelConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={panelAlertContentClass}>
        <AlertDialogTitle className="sr-only">{title}</AlertDialogTitle>
        <AlertDialogDescription className="sr-only">{description}</AlertDialogDescription>
        <PainelCompactDialogShell>
          <PainelFeedbackDialogHeader tone={tone} title={title} description={description} />
          <PainelFeedbackDialogFooter
            tone={tone}
            cancelLabel={cancelLabel}
            confirmLabel={confirmLabel}
            onConfirm={onConfirm}
          />
        </PainelCompactDialogShell>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export type PainelSuccessDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
};

export function PainelSuccessDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'OK',
}: PainelSuccessDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={panelAlertContentClass} showCloseButton={false}>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <PainelCompactDialogShell>
          <PainelFeedbackDialogHeader tone="success" title={title} description={description} />
          <div className="flex justify-end border-t border-white/10 bg-blue-950/25 px-6 py-4">
            <Button
              type="button"
              className={cn(
                painelButtonPrimaryClass,
                'h-10 min-w-[120px] px-5 ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0'
              )}
              onClick={() => onOpenChange(false)}
            >
              {confirmLabel}
            </Button>
          </div>
        </PainelCompactDialogShell>
      </DialogContent>
    </Dialog>
  );
}
